import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { apiErrorMessage } from "@/lib/apiError";
import { notify } from "@/lib/notify";

const statusOf = (error: unknown) =>
    error instanceof AxiosError ? (error.response?.status ?? 0) : 0;

/**
 * Global error policy:
 * - 401 → never a toast. The axios interceptor in api/client.ts already flips
 *   sessionExpired for real session failures (and knows to skip auth/login and
 *   auth/2fa, where a 401 just means "wrong credentials/code" — handled inline
 *   by the login form instead). Duplicating that check here would race it.
 * - 422 → owned by forms (useApiForm maps field errors); skipped here.
 * - anything else → red notification, unless the call opted out via meta.silent.
 */
function handleGlobalError(error: unknown, meta?: Record<string, unknown>) {
    const status = statusOf(error);
    if (status === 401 || status === 422 || meta?.silent || meta?.form) return;
    notify.err(apiErrorMessage(error));
}

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => handleGlobalError(error, query.meta),
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) =>
            handleGlobalError(error, mutation.meta),
    }),
    defaultOptions: {
        queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: true,
            retry: (failureCount, error) =>
                failureCount < 2 && ![401, 403, 404, 422].includes(statusOf(error)),
        },
        mutations: {
            retry: false,
        },
    },
});

/**
 * Drops every cached query except the two that are the same in every workspace: `whoami`
 * and the workspace list. Run it when the active workspace changes — `wsId` travels as the
 * `x-ws-id` header and is never part of a query key, so anything else in the cache may be
 * the previous workspace's answer.
 *
 * ⚠ Not `queryClient.clear()`. That also dropped `whoami`, and `AuthedLayout` renders a
 * full-viewport spinner INSTEAD of the shell while `whoami` is pending — so entering a
 * workspace tore the whole shell down and refetched it, and a hard reload painted spinner →
 * shell → spinner → shell. Clearing the two account-level answers bought nothing: neither
 * depends on the header.
 */
const WORKSPACE_INDEPENDENT = new Set<unknown>(["/api/v1/account/whoami", "/api/v1/workspaces"]);

export function clearWorkspaceQueries() {
    queryClient.removeQueries({ predicate: (query) => !WORKSPACE_INDEPENDENT.has(query.queryKey[0]) });
}
